<script setup>
import ModalWrapper from '@/modals/ModalWrapper';
import { ref, watch } from 'vue';
import { useTireSetStore } from '@/stores/tireSet';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';

const emit = defineEmits(['close-modal', 'cancel-modal']);

const { tireBrands, customerOptions, binOptions } = storeToRefs(useOptionsStore());
const { checkIn, customerVehicles } = useTireSetStore();

const useExistingCustomer = ref(customerOptions.value.length > 0);
const useExistingVehicle = ref(false);
const vehicleOptions = ref([]);

const form = ref({
    customer_hash: null,
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    vehicle_hash: null,
    vin: '',
    make: '',
    model: '',
    year: null,
    quantity: 4,
    season: 'winter',
    tire_brand_id: null,
    tire_model: '',
    width: null,
    aspect_ratio: null,
    diameter: null,
    tread_depth: null,
    has_rims: false,
    description: '',
    bin_id: null,
});

watch(() => form.value.customer_hash, async (hash) => {
    vehicleOptions.value = [];
    form.value.vehicle_hash = null;
    useExistingVehicle.value = false;

    if (hash) {
        const { data } = await customerVehicles(hash);
        vehicleOptions.value = (data.vehicles ?? []).map((vehicle) => ({
            label: vehicle.label,
            value: vehicle.hash,
        }));
        useExistingVehicle.value = vehicleOptions.value.length > 0;
    }
});

const save = async () => {
    try {
        await checkIn({
            ...form.value,
            customer_hash: useExistingCustomer.value ? form.value.customer_hash : null,
            vehicle_hash: useExistingVehicle.value ? form.value.vehicle_hash : null,
        });

        emit('close-modal');
    } catch (error) {
        // The response interceptor surfaces the error toast
    }
};
</script>
<template>
<ModalWrapper @cancel-modal="emit('cancel-modal')">
    <template #header>{{ $t('label.check_in') }} {{ $t('label.tire_set') }}</template>
    <template #subheader>{{ $t('label.create_or_edit', { what: $t('label.tire_set') }) }}</template>

    <form @submit.prevent="save">
        <h4 class="text-xs font-semibold uppercase tracking-wider text-ink-light mb-2">{{ $t('label.customer') }}</h4>
        <StyledRoundSwitch
            v-if="customerOptions.length"
            v-model="useExistingCustomer"
            :label="$t('label.existing_customer')"
            inset
            name="existing_customer" />
        <div class="grid md:grid-cols-2 gap-x-4 gap-y-3 mt-2">
            <StyledDropdown
                v-if="useExistingCustomer"
                v-model="form.customer_hash"
                class="md:col-span-2"
                :options="customerOptions"
                name="customer_hash"
                :label="$t('label.customer')"
                :placeholder="$t('label.customer')" />
            <template v-else>
                <StyledInput
                    v-model="form.first_name"
                    name="first_name"
                    :placeholder="$t('label.first_name')"
                    :label="$t('label.first_name')" />
                <StyledInput
                    v-model="form.last_name"
                    name="last_name"
                    :placeholder="$t('label.last_name')"
                    :label="$t('label.last_name')" />
                <StyledInput
                    v-model="form.email"
                    name="email"
                    :placeholder="$t('label.email')"
                    :label="$t('label.email')" />
                <StyledInput
                    v-model="form.phone"
                    name="phone"
                    :placeholder="$t('label.phone')"
                    :label="$t('label.phone')" />
            </template>
        </div>

        <h4 class="text-xs font-semibold uppercase tracking-wider text-ink-light mt-5 mb-2">{{ $t('label.vehicle') }}</h4>
        <StyledRoundSwitch
            v-if="vehicleOptions.length"
            v-model="useExistingVehicle"
            :label="$t('label.existing_vehicle')"
            inset
            name="existing_vehicle" />
        <div class="grid md:grid-cols-4 gap-x-4 gap-y-3 mt-2">
            <StyledDropdown
                v-if="useExistingVehicle"
                v-model="form.vehicle_hash"
                class="md:col-span-4"
                :options="vehicleOptions"
                name="vehicle_hash"
                :label="$t('label.vehicle')"
                :placeholder="$t('label.vehicle')" />
            <template v-else>
                <StyledInput
                    v-model.number="form.year"
                    name="year"
                    type="number"
                    :placeholder="$t('label.year')"
                    :label="$t('label.year')" />
                <StyledInput
                    v-model="form.make"
                    name="make"
                    :placeholder="$t('label.make')"
                    :label="$t('label.make')" />
                <StyledInput
                    v-model="form.model"
                    name="model"
                    :placeholder="$t('label.vehicle_model')"
                    :label="$t('label.vehicle_model')" />
                <StyledInput
                    v-model="form.vin"
                    name="vin"
                    :placeholder="$t('label.vin')"
                    :label="$t('label.vin')" />
            </template>
        </div>

        <h4 class="text-xs font-semibold uppercase tracking-wider text-ink-light mt-5 mb-2">{{ $t('label.set_details') }}</h4>
        <div class="grid md:grid-cols-4 gap-x-4 gap-y-3">
            <StyledInput
                v-model.number="form.quantity"
                name="quantity"
                type="number"
                :placeholder="$t('label.quantity')"
                :label="$t('label.quantity')" />
            <StyledDropdown
                v-model="form.season"
                :options="[
                    { label: $t('label.winter'), value: 'winter' },
                    { label: $t('label.summer'), value: 'summer' },
                    { label: $t('label.all_season'), value: 'all_season' },
                ]"
                name="season"
                :label="$t('label.season')"
                :placeholder="$t('label.season')" />
            <StyledDropdown
                v-model="form.tire_brand_id"
                clearable
                :options="tireBrands"
                name="tire_brand_id"
                :label="$t('label.brand')"
                :placeholder="$t('label.brand')" />
            <StyledInput
                v-model="form.tire_model"
                name="tire_model"
                :placeholder="$t('label.tire_model')"
                :label="$t('label.tire_model')" />
            <StyledInput
                v-model.number="form.width"
                name="width"
                type="number"
                :placeholder="$t('label.width')"
                :label="$t('label.width')" />
            <StyledInput
                v-model.number="form.aspect_ratio"
                name="aspect_ratio"
                type="number"
                :placeholder="$t('label.aspect_ratio')"
                :label="$t('label.aspect_ratio')" />
            <StyledInput
                v-model.number="form.diameter"
                name="diameter"
                type="number"
                :placeholder="$t('label.diameter')"
                :label="$t('label.diameter')" />
            <StyledInput
                v-model.number="form.tread_depth"
                name="tread_depth"
                type="number"
                :placeholder="$t('label.tread_depth')"
                :label="$t('label.tread_depth')" />
        </div>
        <div class="grid md:grid-cols-2 gap-x-4 gap-y-3 mt-2 items-center">
            <StyledRoundSwitch
                v-model="form.has_rims"
                :label="$t('label.on_rims')"
                inset
                name="has_rims" />
            <StyledInput
                v-model="form.description"
                name="description"
                :placeholder="$t('label.description')"
                :label="$t('label.description')" />
        </div>

        <div
            v-if="binOptions.length"
            class="mt-3">
            <StyledDropdown
                v-model="form.bin_id"
                clearable
                :options="binOptions"
                name="bin_id"
                :label="$t('label.assign_to_bin')"
                :placeholder="$t('label.assign_to_bin')" />
        </div>
    </form>

    <template #footer>
        <StyledSaveButton modal @save="save" />
    </template>
</ModalWrapper>
</template>
